import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText, Download, RefreshCw } from "lucide-react";
import { api } from "@/lib/api-client";
import { toRiskScore } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { toast } from "@/components/ui/Toast";
import { useTranslation } from "@/hooks/useTranslation";
import type { RiskLevel, AssessmentStatus } from "@/types";

// ─────────────────────────────────────────────
// ReportTab
//
// Tab 6 — formatted report view with Generate and Export PDF.
// Design: §6.3 Tab 6 — Report
// PRD §6.3 Report Output Format
// ─────────────────────────────────────────────

interface ReportSection {
  postureName: string;
  postureCode: string;
  score: number;
  riskLevel: RiskLevel;
  answered: number;
  total: number;
  keyFindings?: string[];
}

interface RecommendationItem {
  priority: "critical" | "high" | "medium" | "low";
  title: string;
  description: string;
  questionCode?: string;
}

interface ReportData {
  entityName: string;
  version: number;
  assessmentDate: string;
  assessorName?: string;
  documentStatus: AssessmentStatus;
  overallScore: number;
  riskLevel: RiskLevel;
  executiveSummary?: string;
  topRiskAreas?: string[];
  sectionScores?: ReportSection[];
  recommendations?: RecommendationItem[];
  securityComment?: string;
  securityCommentReviewer?: string;
  metadata?: Record<string, string | null | undefined>;
}

// ─────────────────────────────────────────────
// API raw shape (what the server actually returns)
// ─────────────────────────────────────────────

interface ApiReportResponse {
  cover?: {
    entityName?: string;
    generatedAt?: string;
    generatedBy?: string;
  };
  executiveSummary?: {
    overallScore?: number;
    overallLevel?: string;
    status?: string;
    keyFindings?: string[];
  };
  metadata?: {
    currentVersion?: number;
    schemaName?: string;
    [key: string]: unknown;
  };
  sectionScores?: Array<{
    postureName?: string;
    postureCode?: string;
    score?: number;
    level?: string;
    riskLevel?: string;
    answered?: number;
    total?: number;
    keyFindings?: string[];
  }>;
  recommendations?: Array<string | { priority?: string; title?: string; description?: string; questionCode?: string }>;
  securityComment?: {
    available?: boolean;
    message?: string;
  } | string | null;
  riskMatrix?: {
    overallScore?: number;
    overallLevel?: string;
  };
}

const VALID_PRIORITIES = new Set(["critical", "high", "medium", "low"]);

function inferPriorityFromText(text: string): "critical" | "high" | "medium" | "low" {
  const lower = text.toLowerCase();
  if (lower.includes("critical")) return "critical";
  if (lower.includes("high")) return "high";
  if (lower.includes("medium")) return "medium";
  if (lower.includes("low")) return "low";
  return "medium";
}

function normalizeRiskLevel(raw: string | undefined | null): RiskLevel {
  const upper = (raw ?? "").toUpperCase();
  if (upper === "CRITICAL" || upper === "VERY_HIGH" || upper === "HIGH" || upper === "MEDIUM" || upper === "LOW") {
    return upper as RiskLevel;
  }
  return "MEDIUM";
}

function normalizeRecommendation(
  item: string | { priority?: string; title?: string; description?: string; questionCode?: string },
  index: number
): RecommendationItem {
  if (typeof item === "string") {
    return {
      priority: inferPriorityFromText(item),
      title: `Recommendation ${index + 1}`,
      description: item,
    };
  }
  const rawPriority = (item.priority ?? "").toLowerCase();
  return {
    priority: VALID_PRIORITIES.has(rawPriority)
      ? (rawPriority as RecommendationItem["priority"])
      : inferPriorityFromText(item.title ?? item.description ?? ""),
    title: item.title ?? `Recommendation ${index + 1}`,
    description: item.description ?? "",
    questionCode: item.questionCode,
  };
}

function normalizeReport(raw: ApiReportResponse): ReportData {
  const cover = raw.cover ?? {};
  const execSummary = raw.executiveSummary ?? {};
  const meta = raw.metadata ?? {};

  // Flatten metadata for display — exclude known structural keys
  const EXCLUDED_META_KEYS = new Set([
    "assessmentId", "entityId", "schemaId", "schemaVersionId",
  ]);
  const displayMetadata: Record<string, string | null | undefined> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (!EXCLUDED_META_KEYS.has(k) && v != null && typeof v !== "object") {
      displayMetadata[k] = String(v);
    }
  }

  // securityComment can be an object or string
  let securityCommentText: string | undefined;
  if (typeof raw.securityComment === "string") {
    securityCommentText = raw.securityComment || undefined;
  } else if (raw.securityComment?.available && raw.securityComment?.message) {
    securityCommentText = raw.securityComment.message;
  }

  const sectionScores: ReportSection[] = (raw.sectionScores ?? []).map((sec) => ({
    postureName: sec.postureName ?? "",
    postureCode: sec.postureCode ?? "",
    score: sec.score ?? 0,
    riskLevel: normalizeRiskLevel(sec.riskLevel ?? sec.level),
    answered: sec.answered ?? 0,
    total: sec.total ?? 0,
    keyFindings: sec.keyFindings,
  }));

  const recommendations: RecommendationItem[] = (raw.recommendations ?? []).map(
    (item, i) => normalizeRecommendation(item, i)
  );

  return {
    entityName: cover.entityName ?? meta.entityName as string ?? "Unknown Entity",
    version: (meta.currentVersion as number) ?? 1,
    assessmentDate: cover.generatedAt ?? new Date().toISOString(),
    assessorName: undefined, // generatedBy is a user ID — omit until we have user resolution
    documentStatus: (execSummary.status ?? meta.status ?? "DRAFT") as AssessmentStatus,
    overallScore: execSummary.overallScore ?? raw.riskMatrix?.overallScore ?? 0,
    riskLevel: normalizeRiskLevel(execSummary.overallLevel ?? raw.riskMatrix?.overallLevel),
    executiveSummary: undefined, // API has no free-text executive summary
    topRiskAreas: execSummary.keyFindings,
    sectionScores,
    recommendations,
    securityComment: securityCommentText,
    metadata: Object.keys(displayMetadata).length > 0 ? displayMetadata : undefined,
  };
}

interface ReportTabProps {
  assessmentId: string;
  status: AssessmentStatus;
}


export function ReportTab({ assessmentId, status }: ReportTabProps) {
  const { t } = useTranslation();
  // Terminal means APPROVED or REJECTED — report is viewable but not regeneratable
  const isTerminal = status === "APPROVED" || status === "REJECTED";
  // Active statuses allow generation/regeneration
  const canGenerate = !isTerminal;

  // Fetch existing report on mount — regardless of status (404 = no report yet, that's OK)
  const { data: fetchedReport, refetch } = useQuery<ReportData | null>({
    queryKey: ["report", assessmentId],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: ApiReportResponse }>(`/reports/assessments/${assessmentId}`);
        return normalizeReport(res.data ?? res as unknown as ApiReportResponse);
      } catch {
        // 404 means no report exists yet — treat as null
        return null;
      }
    },
    retry: false,
  });

  // The report comes from the normalized fetched data
  const currentReport = fetchedReport ?? null;

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: ApiReportResponse }>(`/reports/assessments/${assessmentId}`),
    onSuccess: () => {
      refetch();
      toast.success(t("reports.reportGenerated"));
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("reports.generateFailed"));
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/reports/assessments/${assessmentId}/export`, {
        credentials: "include",
      });
      if (!res.ok) {
        // Surface the server error message if available
        let errMsg = t("reports.exportFailedStatus", { status: res.status });
        try {
          const body = await res.json() as { error?: { message?: string } };
          if (body?.error?.message) errMsg = body.error.message;
        } catch { /* ignore parse errors */ }
        throw new Error(errMsg);
      }
      const ct = res.headers.get("Content-Type") ?? "";
      if (!ct.includes("application/pdf")) {
        // Server returned something other than a PDF — do not save as .pdf
        throw new Error(t("reports.pdfInvalid"));
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-${assessmentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast.success(t("reports.reportExported"));
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("reports.exportFailed"));
    },
  });

  if (!currentReport) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: "var(--bg-hover)" }}
        >
          <FileText className="h-8 w-8 text-text-muted" />
        </div>
        <div className="text-center">
          <p className="text-[16px] font-semibold text-text-primary">{t("reports.noReportGenerated")}</p>
          <p className="mt-2 text-[14px] text-text-muted">
            {isTerminal
              ? t("reports.noReportFinalised")
              : t("reports.generatePrompt")}
          </p>
        </div>
        {canGenerate && (
          <Button
            onClick={() => generateMutation.mutate()}
            isLoading={generateMutation.isPending}
            leadingIcon={<RefreshCw className="h-4 w-4" />}
          >
            {t("reports.generateReport")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Actions bar */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[18px] font-semibold text-text-primary">{t("reports.riskAssessmentReport")}</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateMutation.mutate()}
            isLoading={generateMutation.isPending}
            disabled={!canGenerate}
            leadingIcon={<RefreshCw className="h-4 w-4" />}
            title={isTerminal ? t("reports.cannotRegenerate") : undefined}
          >
            {t("vendor.regenerate")}
          </Button>
          <Button
            size="sm"
            onClick={() => exportMutation.mutate()}
            isLoading={exportMutation.isPending}
            leadingIcon={<Download className="h-4 w-4" />}
          >
            {t("common.exportPdf")}
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      {(currentReport.executiveSummary || (currentReport.topRiskAreas && currentReport.topRiskAreas.length > 0)) && (
        <ReportSection title={t("reports.executiveSummary")}>
          {currentReport.executiveSummary && (
            <p className="text-[14px] text-text-secondary">{currentReport.executiveSummary}</p>
          )}
          {currentReport.topRiskAreas && currentReport.topRiskAreas.length > 0 && (
            <div className="mt-3">
              <p className="text-[13px] font-semibold text-text-muted mb-2">{t("reports.topRiskAreas")}</p>
              <ul className="flex flex-col gap-1">
                {currentReport.topRiskAreas.map((area, i) => (
                  <li key={i} className="flex items-center gap-2 text-[14px] text-text-secondary">
                    <span
                      className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: "var(--k-orange)" }}
                    />
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ReportSection>
      )}

      {/* Application Metadata */}
      {currentReport.metadata && Object.keys(currentReport.metadata).length > 0 && (
        <ReportSection title={t("assessment.applicationMetadata")}>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 md:grid-cols-3">
            {Object.entries(currentReport.metadata).map(([k, v]) => (
              v ? (
                <div key={k} className="flex flex-col gap-0.5">
                  <span className="text-[12px] font-medium uppercase tracking-wide text-text-muted">
                    {k.replace(/([A-Z])/g, " $1").trim()}
                  </span>
                  <span className="text-[14px] text-text-secondary">{String(v)}</span>
                </div>
              ) : null
            ))}
          </div>
        </ReportSection>
      )}

      {/* Section Scores */}
      {currentReport.sectionScores && currentReport.sectionScores.length > 0 && (
        <ReportSection title={t("reports.sectionScores")}>
          <div className="flex flex-col gap-4">
            {currentReport.sectionScores.map((sec) => (
              <div key={sec.postureCode} className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[14px] font-medium text-text-primary">{sec.postureName}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-text-muted">
                      {t("reports.answeredCount", { answered: sec.answered, total: sec.total })}
                    </span>
                    <StatusBadge type="risk" status={sec.riskLevel} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 overflow-hidden rounded-full" style={{ backgroundColor: "var(--bg-hover)" }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${toRiskScore(sec.score)}%`,
                        backgroundColor: "var(--accent)",
                      }}
                    />
                  </div>
                  <span className="w-10 text-right text-[12px] text-text-muted">
                    {toRiskScore(sec.score)}%
                  </span>
                </div>
                {sec.keyFindings && sec.keyFindings.length > 0 && (
                  <ul className="flex flex-col gap-0.5 pl-2">
                    {sec.keyFindings.map((f, fi) => (
                      <li key={fi} className="text-[13px] text-text-muted">{f}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </ReportSection>
      )}

      {/* Security Comment */}
      {currentReport.securityComment && (
        <ReportSection title={t("assessment.approveDialog.commentLabel")}>
          <div
            className="rounded-card p-4"
            style={{
              backgroundColor: "var(--success-bg)",
              borderLeft: "3px solid var(--success)",
            }}
          >
            <p className="text-[14px] text-text-secondary">{currentReport.securityComment}</p>
            {currentReport.securityCommentReviewer && (
              <p className="mt-2 text-[12px] text-text-muted">
                - {currentReport.securityCommentReviewer}
              </p>
            )}
          </div>
        </ReportSection>
      )}
    </div>
  );
}

// Internal section wrapper
function ReportSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-border-default bg-card p-5">
      <h3 className="mb-4 text-[15px] font-semibold text-text-primary">{title}</h3>
      {children}
    </div>
  );
}
